import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, ArrowRight, Lightbulb, Sparkles, Check } from "lucide-react";
import { Button } from "../../components/ui/button";

const ArticleColoringPagePrompts = () => {
  const prompts = {
    animals: [
      "A friendly lion playing with butterflies in a sunny meadow",
      "Cute baby elephants splashing in a watering hole",
      "A wise owl reading a book on a tree branch at night",
      "Playful dolphins jumping over ocean waves",
      "A family of pandas eating bamboo in a forest",
      "Adorable kittens playing with yarn balls",
      "A majestic horse running through flower fields",
      "Funny monkeys swinging from jungle vines",
      "A gentle giraffe eating leaves from tall trees",
      "Colorful tropical birds in a rainforest"
    ],
    fantasy: [
      "A magical unicorn with rainbow mane in enchanted garden",
      "Friendly dragon blowing bubble instead of fire",
      "Fairy princess with butterfly wings in flower castle",
      "Mermaid swimming with fish friends underwater",
      "Wizard cat casting sparkle spells",
      "Flying pegasus above fluffy clouds",
      "Gnomes building tiny mushroom houses",
      "Phoenix bird rising with beautiful feathers",
      "Enchanted treehouse in magical forest",
      "Crystal cave with glowing gems and fairies"
    ],
    space: [
      "Astronaut floating among friendly planets",
      "Rocket ship zooming past smiling stars",
      "Alien family having a picnic on the moon",
      "Space station with kids looking at Earth",
      "Robot exploring Mars with curiosity",
      "Saturn with its beautiful rings up close",
      "Spaceship racing through asteroid field",
      "Cute aliens waving from their UFO",
      "Telescope pointed at constellation animals",
      "Moon base with astronauts planting flag"
    ],
    nature: [
      "Peaceful waterfall in tropical jungle",
      "Butterfly garden with hundreds of flowers",
      "Cozy treehouse in autumn forest",
      "Rainbow over mountain landscape",
      "Underwater coral reef with sea creatures",
      "Cherry blossom trees in Japanese garden",
      "Camping scene with tent under starry sky",
      "Sunflower field with happy bees",
      "Snowy forest with deer and rabbits",
      "Beach scene with sandcastle and shells"
    ],
    vehicles: [
      "Fire truck rushing to help with sirens on",
      "Train going through mountain tunnel",
      "Hot air balloons floating over countryside",
      "Submarine exploring deep ocean",
      "Monster truck jumping over cars",
      "Helicopter rescuing stranded hikers",
      "Pirate ship sailing on adventure",
      "Race cars zooming around track",
      "Construction vehicles building playground",
      "School bus picking up happy children"
    ]
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full font-medium">Prompts & Ideas</span>
              <span>•</span>
              <span>Jan 2026</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                8 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              50 Free Coloring Page Prompts for Kids [2026]
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Running out of ideas for coloring pages? We've compiled 50 creative, kid-friendly prompts that spark imagination and create beautiful printable pages. Use these with ColorWonder to generate unique coloring books in seconds!
            </p>
          </header>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12">
            <h2 className="font-semibold text-slate-800 mb-4">📋 Table of Contents</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#why-prompts" className="hover:text-violet-600">Why Good Prompts Matter</a></li>
              <li><a href="#animal-prompts" className="hover:text-violet-600">🦁 Animal Coloring Page Prompts (10)</a></li>
              <li><a href="#fantasy-prompts" className="hover:text-violet-600">🦄 Fantasy & Magic Prompts (10)</a></li>
              <li><a href="#space-prompts" className="hover:text-violet-600">🚀 Space Adventure Prompts (10)</a></li>
              <li><a href="#nature-prompts" className="hover:text-violet-600">🌸 Nature & Outdoors Prompts (10)</a></li>
              <li><a href="#vehicle-prompts" className="hover:text-violet-600">🚗 Vehicle & Transportation Prompts (10)</a></li>
              <li><a href="#tips" className="hover:text-violet-600">Pro Tips for Better Prompts</a></li>
              <li><a href="#colorwonder" className="hover:text-violet-600">Try These Prompts with ColorWonder</a></li>
              <li><a href="#faq" className="hover:text-violet-600">Frequently Asked Questions</a></li>
            </ul>
          </div>

          {/* Introduction */}
          <section className="prose prose-lg max-w-none mb-12">
            <p>
              Finding the perfect coloring page for your child can be frustrating. You spend hours searching online, only to find the same generic designs everyone else has. What if you could create <strong>unique, personalized coloring pages</strong> that match exactly what your child loves?
            </p>
            <p>
              That's where good prompts come in. With AI coloring page generators like ColorWonder, a well-crafted prompt can turn any idea into a beautiful, printable coloring page. In this guide, we're sharing <strong>50 tested prompts</strong> that consistently create amazing results.
            </p>
          </section>

          {/* Why Prompts Matter */}
          <section id="why-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Good Prompts Matter
            </h2>
            <p className="text-slate-600 mb-4">
              The difference between a boring coloring page and an amazing one often comes down to the prompt. Here's what makes a great coloring page prompt:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Be Specific", desc: "Instead of 'cat', try 'playful kitten with yarn'" },
                { title: "Add Action", desc: "Characters doing something are more engaging" },
                { title: "Include Setting", desc: "Where is the scene happening?" },
                { title: "Keep it Age-Appropriate", desc: "Simple for toddlers, detailed for older kids" },
              ].map((tip, i) => (
                <div key={i} className="bg-violet-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-800 mb-1">{tip.title}</h4>
                  <p className="text-slate-600 text-sm">{tip.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Animal Prompts */}
          <section id="animal-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🦁 Animal Coloring Page Prompts
            </h2>
            <p className="text-slate-600 mb-6">Kids love animals! These prompts create adorable, engaging coloring pages featuring favorite creatures.</p>
            
            <div className="space-y-3">
              {prompts.animals.map((prompt, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Fantasy Prompts */}
          <section id="fantasy-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🦄 Fantasy & Magic Prompts
            </h2>
            <p className="text-slate-600 mb-6">Spark imagination with magical creatures and enchanted worlds perfect for creative kids.</p>
            
            <div className="space-y-3">
              {prompts.fantasy.map((prompt, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-8 h-8 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 11}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Space Prompts */}
          <section id="space-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🚀 Space Adventure Prompts
            </h2>
            <p className="text-slate-600 mb-6">Blast off into the cosmos with these space-themed prompts that inspire wonder and curiosity.</p>
            
            <div className="space-y-3">
              {prompts.space.map((prompt, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 21}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Nature Prompts */}
          <section id="nature-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🌸 Nature & Outdoors Prompts
            </h2>
            <p className="text-slate-600 mb-6">Beautiful natural scenes that help kids appreciate the world around them while coloring.</p>
            
            <div className="space-y-3">
              {prompts.nature.map((prompt, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 31}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Vehicle Prompts */}
          <section id="vehicle-prompts" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🚗 Vehicle & Transportation Prompts
            </h2>
            <p className="text-slate-600 mb-6">Action-packed vehicles that kids love to color - from fire trucks to spaceships!</p>
            
            <div className="space-y-3">
              {prompts.vehicles.map((prompt, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 41}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Pro Tips */}
          <section id="tips" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              💡 Pro Tips for Better Prompts
            </h2>
            <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-6 space-y-4">
              {[
                "Add adjectives like 'cute', 'happy', 'friendly' for kid-appropriate results",
                "Mention 'coloring page style' or 'line art' for cleaner outlines",
                "Specify age group: 'simple for toddlers' or 'detailed for teens'",
                "Combine themes: 'dinosaur astronaut' or 'mermaid princess'",
                "Include emotions: 'excited puppy' or 'peaceful garden'"
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700">{tip}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ColorWonder CTA */}
          <section id="colorwonder" className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-6 h-6" />
                <span className="font-semibold">Try These Prompts with ColorWonder</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Turn Any Prompt Into a Printable Coloring Page
              </h3>
              <p className="text-violet-100 mb-6">
                ColorWonder uses AI to transform your ideas into beautiful, print-ready coloring pages. Simply type any prompt from this list (or create your own) and watch the magic happen!
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => window.location.href = "/signup"}
                  className="h-12 px-6 rounded-full bg-white text-violet-600 font-semibold hover:bg-violet-50"
                >
                  Try ColorWonder Free →
                </Button>
                <div className="flex items-center gap-2 text-violet-100">
                  <Check className="w-5 h-5" />
                  <span>10 free pages/month</span>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Can I use these prompts with any AI coloring page generator?",
                  a: "Yes! These prompts work with most AI image generators. However, they're optimized for ColorWonder which is specifically designed for creating coloring pages."
                },
                {
                  q: "Are these coloring pages free to print?",
                  a: "When you create pages with ColorWonder, you can print them for personal use. The free plan includes 10 pages per month. Paid plans offer more pages and commercial licenses."
                },
                {
                  q: "What age are these prompts best for?",
                  a: "Most prompts work for ages 4-12. You can adjust complexity by adding 'simple' for younger kids or 'detailed' for older children."
                },
                {
                  q: "Can I modify these prompts?",
                  a: "Absolutely! These are starting points. Feel free to add your child's favorite characters, colors they want to see, or combine multiple prompts."
                },
                {
                  q: "How do I get the best results?",
                  a: "Be specific about what you want, mention it's for a coloring page, and include actions or settings. ColorWonder is trained to create clean line art perfect for coloring."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Start Creating Today!
            </h2>
            <p className="text-slate-600 mb-4">
              With these 50 prompts, you'll never run out of coloring page ideas. Whether your child loves animals, space adventures, or magical unicorns, there's something here for everyone.
            </p>
            <p className="text-slate-600">
              Ready to bring these ideas to life? <Link to="/signup" className="text-violet-600 font-semibold hover:underline">Try ColorWonder free</Link> and start creating custom coloring pages in seconds. Your kids will love having unique pages made just for them!
            </p>
          </section>

          {/* Related Articles */}
          <section className="border-t border-slate-200 pt-12">
            <h3 className="text-xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Related Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/create-coloring-pages-with-ai" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">How to Create Coloring Pages with AI</h4>
                <p className="text-slate-500 text-sm">Step-by-step tutorial for beginners</p>
              </Link>
              <Link to="/blog/custom-coloring-pages" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">Custom Coloring Pages in Seconds</h4>
                <p className="text-slate-500 text-sm">Make personalized pages for any occasion</p>
              </Link>
            </div>
          </section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 ColorWonder. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticleColoringPagePrompts;
